import { FeatureFlagsProvider } from "@evidenceb/athena-common/modules/FeatureFlags";
import { MathJaxProvider } from "@evidenceb/athena-common/modules/HTMLParser";
import { useEffect } from "react";
import { ConfigProvider } from "./contexts/ConfigContext";
import { ContentPagesProvider } from "./contexts/ContentPagesContext";
import { DataProvider } from "./contexts/DataContext";
import { HomeProvider } from "./contexts/HomeContext";
import Root from "./router/Root";
import "./scss/App.scss";
import { useMatomo } from "@jonkoops/matomo-tracker-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary } from "react-error-boundary";
import { HelmetProvider } from "react-helmet-async";
import { ConfigOverrideBanner } from "./components/ConfigOverrideBanner/ConfigOverrideBanner";
import { BlockingLoaderProvider } from "./contexts/BlockingLoaderContext";
import { ErrorProvider } from "./contexts/ErrorContext";
import { EventStreamProvider } from "./contexts/EventStreamContext";
import LangProvider from "./contexts/LangContext";
import { ModuleCachingStateProvider } from "./contexts/OfflineCache/useModuleCaching/ModuleCacheManagerContext";
import { OnlineStatusProvider } from "./contexts/OnlineStatusContext";
import { SessionProvider } from "./contexts/SessionContext";
import { SessionGuardProvider } from "./contexts/SessionGuardProvider";
import { SyncProvider } from "./contexts/Sync";
import ErrorFallback from "./pages/ErrorFallback/ErrorFallback";

const App = () => {
  // Remove React.StrictMode
  const { enableLinkTracking, trackPageView } = useMatomo();

  const queryClient = new QueryClient();

  enableLinkTracking();
  useEffect(() => {
    trackPageView({});
  }, [trackPageView]);

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorProvider>
        <DataProvider>
          <ConfigProvider>
            <FeatureFlagsProvider>
              <HomeProvider>
                <ContentPagesProvider>
                  <SessionProvider>
                    <EventStreamProvider>
                      <MathJaxProvider>
                        <HelmetProvider>
                          <SyncProvider>
                            <LangProvider>
                              <OnlineStatusProvider>
                                <SessionGuardProvider>
                                  <ErrorBoundary
                                    FallbackComponent={ErrorFallback}
                                  >
                                    <BlockingLoaderProvider>
                                      <ModuleCachingStateProvider>
                                        <ConfigOverrideBanner />
                                        <Root />
                                      </ModuleCachingStateProvider>
                                    </BlockingLoaderProvider>
                                  </ErrorBoundary>
                                </SessionGuardProvider>
                              </OnlineStatusProvider>
                            </LangProvider>
                          </SyncProvider>
                        </HelmetProvider>
                      </MathJaxProvider>
                    </EventStreamProvider>
                  </SessionProvider>
                </ContentPagesProvider>
              </HomeProvider>
            </FeatureFlagsProvider>
          </ConfigProvider>
        </DataProvider>
      </ErrorProvider>
    </QueryClientProvider>
  );
};

export default App;
